import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useFirms } from "@/lib/queries";
import {
  MAX_RULES, MAX_SPLIT, RULE_OPS, comparesValues, opTerm, splitTotal, type RoutingValues, type RuleRow,
  type SplitRow,
} from "@/lib/trigger-routing";
import type { Firm } from "@/lib/types";

/**
 * Where a new-lead trigger sends the leads it makes, and which it rules out (ADR-175 in platform): the
 * tags every request is read as carrying, a split of new leads across the tenant's firms, and the
 * answers that make a lead not qualified. A firm with a campaign of its own takes its leads there; the
 * trigger's campaign takes the rest. The rules are `lib/trigger-routing.ts`.
 */
export function TriggerRouting({
  value, onChange, enabled,
}: {
  value: RoutingValues;
  onChange: (next: RoutingValues) => void;
  /** Whether the tenant's firms may be read yet. */
  enabled: boolean;
}) {
  const firms = useFirms({ enabled });
  const set = <K extends keyof RoutingValues>(key: K, next: RoutingValues[K]) => onChange({ ...value, [key]: next });
  return (
    <div className="space-y-4" data-testid="trigger-form-routing">
      <Field
        label="Extra tags"
        htmlFor="trigger-extra-tags"
        hint="Tags every request is read as carrying, separated by commas: spanish for a Spanish funnel, which makes its leads Spanish."
      >
        <Input
          id="trigger-extra-tags"
          autoComplete="off"
          placeholder="spanish"
          value={value.extra_tags.join(", ")}
          onChange={(e) => set("extra_tags", e.target.value.split(",").map((tag) => tag.trimStart()))}
        />
      </Field>
      <SplitEditor rows={value.split} onChange={(next) => set("split", next)} firms={firms.data} failed={firms.isError} />
      <RulesEditor rows={value.rules} onChange={(next) => set("rules", next)} />
    </div>
  );
}

function SplitEditor({
  rows, onChange, firms, failed,
}: {
  rows: SplitRow[];
  onChange: (next: SplitRow[]) => void;
  firms: Firm[] | undefined;
  failed: boolean;
}) {
  const total = splitTotal(rows);
  const known = new Set((firms ?? []).map((firm) => String(firm.id)));
  const change = (index: number, patch: Partial<SplitRow>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  return (
    <div className="space-y-2" data-testid="trigger-form-split">
      <div>
        <p className="text-xs font-medium text-text">Firm split</p>
        <p className="text-xs text-text-muted">
          Each new lead goes to one of these firms, as likely as its share. A firm the request names itself wins,
          and a paused firm's share goes to the others. Leave it empty to keep every lead on this trigger's campaign
          with no firm chosen.
        </p>
      </div>
      {failed && <Callout tone="danger">The firms could not be read. Close this and open it again.</Callout>}
      {rows.map((row, index) => (
        <div key={index} className="flex items-center gap-2">
          <Select value={row.firm_id || undefined} onValueChange={(firm_id) => change(index, { firm_id })}>
            <SelectTrigger aria-label={`Firm ${index + 1}`} className="min-w-0 flex-1">
              <SelectValue placeholder="Choose a firm" />
            </SelectTrigger>
            <SelectContent>
              {firms && row.firm_id && !known.has(row.firm_id) && (
                <SelectItem value={row.firm_id}>Firm {row.firm_id}, no longer yours</SelectItem>
              )}
              {(firms ?? []).map((firm) => (
                <SelectItem key={firm.id} value={String(firm.id)}>
                  {firm.label || firm.name} ({firm.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* An Input with `trailing` wraps itself in a full-width box, so the width goes on a box
              of our own; on the Input it reached only the inner field and the firm got squeezed out. */}
          <div className="w-24 shrink-0">
            <Input
              aria-label={`Share of firm ${index + 1}`}
              inputMode="numeric"
              trailing="%"
              value={row.pct}
              onChange={(e) => change(index, { pct: e.target.value })}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Remove firm ${index + 1}`}
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
          >
            <X />
          </Button>
        </div>
      ))}
      <div className="flex items-center justify-between gap-2">
        {rows.length < MAX_SPLIT && (
          <Button type="button" variant="outline" size="sm" onClick={() => onChange([...rows, { firm_id: "", pct: "0" }])}>
            <Plus />
            Add a firm
          </Button>
        )}
        {rows.length > 0 && (
          <p className="text-xs text-text-muted" data-testid="trigger-form-split-total">
            {total === 100 ? "Shares add up to 100%." : `Shares add up to ${total}%; each is its part of the total.`}
          </p>
        )}
      </div>
    </div>
  );
}

function RulesEditor({ rows, onChange }: { rows: RuleRow[]; onChange: (next: RuleRow[]) => void }) {
  const change = (index: number, patch: Partial<RuleRow>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  return (
    <div className="space-y-2" data-testid="trigger-form-rules">
      <div>
        <p className="text-xs font-medium text-text">Not qualified when</p>
        <p className="text-xs text-text-muted">
          A new lead whose answers match a rule is made not qualified: on the campaign to be read, never dialed. The
          first rule that matches gives the reason. Capitals and spacing need not be exact; the words must be the
          funnel's own, in every language it asks in. A lead the platform already has is never changed.
        </p>
      </div>
      {rows.map((row, index) => (
        <div key={index} className="space-y-2 rounded-md border border-border p-3" data-testid={`trigger-form-rule-${index + 1}`}>
          <div className="flex items-center gap-2">
            <Input
              aria-label={`Rule ${index + 1}, answer key`}
              autoComplete="off"
              className="min-w-0 flex-1 font-mono"
              placeholder="is_a_lawyer_already_handling_your_case"
              value={row.field}
              onChange={(e) => change(index, { field: e.target.value })}
            />
            <Select value={row.op} onValueChange={(op) => change(index, { op })}>
              <SelectTrigger aria-label={`Rule ${index + 1}, compares`} className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RULE_OPS.map((op) => (
                  <SelectItem key={op} value={op}>
                    {opTerm(op).label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Remove rule ${index + 1}`}
              onClick={() => onChange(rows.filter((_, i) => i !== index))}
            >
              <X />
            </Button>
          </div>
          {comparesValues(row.op) && (
            <Textarea
              aria-label={`Rule ${index + 1}, answers`}
              rows={2}
              placeholder={"Sí, tengo abogado\nYes, I have a lawyer"}
              value={row.values.join("\n")}
              onChange={(e) => change(index, { values: e.target.value.split("\n") })}
            />
          )}
          <p className="text-xs text-text-muted">
            {opTerm(row.op).description}
            {comparesValues(row.op) ? " One answer per line." : ""}
          </p>
          <Input
            aria-label={`Rule ${index + 1}, reason`}
            autoComplete="off"
            className="font-mono"
            placeholder="has_lawyer"
            value={row.reason}
            onChange={(e) => change(index, { reason: e.target.value })}
          />
        </div>
      ))}
      {rows.length < MAX_RULES && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onChange([...rows, { field: "", op: "in", values: [], reason: "" }])}
        >
          <Plus />
          Add a rule
        </Button>
      )}
    </div>
  );
}
